import * as BABYLON from 'babylonjs';
import * as BABYLONGUI from 'babylonjs-gui';
import PanoViewer from './PanoViewer';
import XmlLoader2 from './XmlLoader2';
import ViewerStore from '../../stores/ViewerStore';
import * as Inovx from '@shared';
import ViewerActions from '../../actions/ViewerActions';
interface ICoordinate {
    realCoordinate: BABYLON.Vector3, 
    imageCoordinate: BABYLON.Vector3,
    screenCoordinate: BABYLON.Vector2
}

interface IMeasurement {
    
    text: any,
    valid: boolean,
    isDirty: boolean,
    lines: BABYLON.Mesh[],
    measureMode: MeasurementType
}
interface ILineInfo extends IMeasurement {
    startPosition: ICoordinate,
    endPosition: ICoordinate,
    textLine: BABYLON.LinesMesh,
    distance:number
}
interface IPointMeasurementInfo extends IMeasurement {
    startPosition: ICoordinate
}

/*
interface ViewerBaseItem extends FacilityInterface.BaseItem {
    id: string;
    title: string;
    checked: boolean;
}

interface ViewerPointMeasurement extends ViewerBaseItem {
    type: 'point';
    startPosition: ICoordinate;
}

interface ViewerDistanceMeasurement extends ViewerBaseItem {
    type: 'distance';
    distance: number;
    startPosition: ICoordinate;
    endPosition: ICoordinate;
}
*/
class MeasurementManager {
    public static readonly DefaultValidColor = "#00FF00";

    private _measurementCounter = 0;
    private _measureMode: MeasurementType = MeasurementType.Point;
    private _viewer: PanoViewer;
    private _guiTexture: BABYLONGUI.AdvancedDynamicTexture;
    private _measurements: IMeasurement[] = [];
    private _currentLine: ILineInfo = null;
    private _validMeasurementColors: BABYLON.Color4[] = [];
    private _invalidMeasurementColors: BABYLON.Color4[] = [];
    private _sphereMeasurementMaterial: BABYLON.ShaderMaterial;
    private _validColor: BABYLON.Color3;
    private get viewer() { return this._viewer; }
    private get scene() { return this._viewer.scene; }
    private get engine() { return this.scene.getEngine(); }

    constructor(viewer: PanoViewer, validColor?: string) {
        this._viewer = viewer;
        this._measureMode = viewer.displayMode as any;
        this._guiTexture = BABYLONGUI.AdvancedDynamicTexture.CreateFullscreenUI('UI');
        
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));
        this._invalidMeasurementColors.push(new BABYLON.Color4(1, 0, 0, 1));

        this._validColor = validColor ? BABYLON.Color3.FromHexString(validColor) : BABYLON.Color3.FromHexString(MeasurementManager.DefaultValidColor);
        
        // Initialize valid measurement colors using the single configurable _validColor
        for (let i = 0; i < 8; i++) {
            this._validMeasurementColors.push(new BABYLON.Color4(this._validColor.r, this._validColor.g, this._validColor.b, 1));
        }

        this.scene.onPointerObservable.add(this._handlePointerObservable);
        this.scene.onBeforeCameraRenderObservable.add(this._handleBeforeCameraRenderObservable);

        

        const vertexShader = `
        precision highp float;

        attribute vec3 position;
        attribute vec3 normal;
        attribute vec2 uv;

        uniform mat4 worldViewProjection;

        void main(void) {
            gl_Position = worldViewProjection * vec4(position, 1.0);
        }
        `;

        const fragmentShader = `
        precision highp float;

        uniform vec3 color;

        void main(void) {
            gl_FragColor = vec4(color, 1.0);
        }
        `;

        const shaderMaterial = new BABYLON.ShaderMaterial("shader", this.scene, {
            vertex: "custom",
            fragment: "custom",
        }, {
            attributes: ["position"],
            uniforms: ["worldViewProjection", "color"],
        });

        // Define the shaders
        BABYLON.Effect.ShadersStore["customVertexShader"] = vertexShader;
        BABYLON.Effect.ShadersStore["customFragmentShader"] = fragmentShader;

        // Set the color uniform in the shader
        shaderMaterial.setColor3("color", this._validColor); // central valid color
        this._sphereMeasurementMaterial = shaderMaterial;
    }

    public dispose() {
        this._clearLines();

        this.scene.onPointerObservable.removeCallback(this._handlePointerObservable);
        this.scene.onBeforeCameraRenderObservable.removeCallback(this._handleBeforeCameraRenderObservable);
    }
    public setMeasurementColor(color: string) {
        if (!color)
            return;
        let newColor: BABYLON.Color3;
        try {
            newColor = BABYLON.Color3.FromHexString(color);
        } catch {
            return; // invalid hex string, ignore
        }
        if (this._validColor && newColor.equals(this._validColor))
            return; // no change

        this._validColor = newColor;

        // Rebuild color arrays
        this._validMeasurementColors.length = 0;
        for (let i = 0; i < 8; i++) {
            this._validMeasurementColors.push(new BABYLON.Color4(this._validColor.r, this._validColor.g, this._validColor.b, 1));
        }

        // Update materials / shader
        if (this._sphereMeasurementMaterial) {
            this._sphereMeasurementMaterial.setColor3("color", this._validColor);
        }
    }
    public setMeasurementMode(mode: MeasurementType) {

        if (this._currentLine != null) {
            this._disposeLineData(this._currentLine);

            this._currentLine = null;
        }

        this._measureMode = mode;
    }
    public setMeasurements(measurements: Measurement[]) {

        this._clearLines();

        this._measurementCounter = measurements.length;

        for (var x = 0; x < measurements.length; x++) {
            if (measurements[x].checked) {
                if (measurements[x].measureMode == MeasurementType.Point) {
                    var pointMeasurement = measurements[x] as PointMeasurement;

                    var pointInfo = this._viewer.worldCoordinateInformation(new BABYLON.Vector3(pointMeasurement.startPosition.x, pointMeasurement.startPosition.y, pointMeasurement.startPosition.z));
                    var posInf: ICoordinate = { realCoordinate: new BABYLON.Vector3(pointMeasurement.startPosition.x, pointMeasurement.startPosition.y, pointMeasurement.startPosition.z), imageCoordinate: pointInfo.imageCoordinate, screenCoordinate: null }
                    var point: IPointMeasurementInfo = { startPosition: posInf, text: null, valid: false, lines: [], isDirty: true, measureMode: MeasurementType.Point };

                    this._measurements.push(point);
                }
                else {
                    var lineMeasurement = measurements[x] as DistanceMeasurement;
                    var startInfo = this._viewer.worldCoordinateInformation(new BABYLON.Vector3(lineMeasurement.startPosition.x, lineMeasurement.startPosition.y, lineMeasurement.startPosition.z));
                    var startCoord: ICoordinate = { realCoordinate: new BABYLON.Vector3(lineMeasurement.startPosition.x, lineMeasurement.startPosition.y, lineMeasurement.startPosition.z), imageCoordinate: startInfo.imageCoordinate, screenCoordinate: null };
                    var endInfo = this._viewer.worldCoordinateInformation(new BABYLON.Vector3(lineMeasurement.endPosition.x, lineMeasurement.endPosition.y, lineMeasurement.endPosition.z));
                    var endCoord: ICoordinate = { realCoordinate: new BABYLON.Vector3(lineMeasurement.endPosition.x, lineMeasurement.endPosition.y, lineMeasurement.endPosition.z), imageCoordinate: endInfo.imageCoordinate, screenCoordinate: null }
                    var distance: ILineInfo = { distance: lineMeasurement.distance, startPosition: startCoord, endPosition: endCoord, text: null, valid: false, lines: [], textLine: null, isDirty: true, measureMode: lineMeasurement.measureMode as any };
                    this._measurements.push(distance);
                }
            }
        }
    }
    public clearMeasurements() {
        this._clearLines();
    }
    private _clearLines() {
        this._measurements.forEach(measurement => {
            this._disposeMeasurement(measurement);
        });

        if (this._currentLine != null) {
            this._disposeMeasurement(this._currentLine);
        }
        this._measurements = [];
    }

    private _pickPoint(evt: BABYLON.PointerInfo): ICoordinate {
        var canvasRect = this.engine.getRenderingCanvasClientRect();
        var pointerX = evt.event.clientX - canvasRect.left;
        var pointerY = evt.event.clientY - canvasRect.top;

        const pickResult = this.scene.pick(pointerX, pointerY);

        if (pickResult.hit) {
            var worldCoord = this.viewer.convertScreenToLocalCoordinate(pointerX, pointerY);
            return { realCoordinate: worldCoord, imageCoordinate: pickResult.pickedPoint, screenCoordinate: new BABYLON.Vector2(pointerX, pointerY) };
        }

        return null;
    }
    private async _createMeasurement(measurement: IMeasurement) {

        var jsonMeasurement = measurement.measureMode == MeasurementType.Point
            ? { name: InovxLoader.t('facility:measurement.coordinate'), measureMode: measurement.measureMode, startPosition: (measurement as any).startPosition.realCoordinate }
            : { name: InovxLoader.t('facility:measurement.dimension'), measureMode: measurement.measureMode, startPosition: (measurement as any).startPosition.realCoordinate, endPosition: (measurement as any).endPosition.realCoordinate, distance: (measurement as any).distance };

        (jsonMeasurement as any).id = "TRANS_" + this._measurementCounter.toString();
        this._measurementCounter++;

        (jsonMeasurement as any).saved = false;
        (jsonMeasurement as any).dirty = true;
        (jsonMeasurement as any).checked = true;

        await ViewerActions.createMeasurement(jsonMeasurement as any);
        await ViewerActions.resetDisplayMode();
    }
    private _handlePointerObservable = (evt: BABYLON.PointerInfo) => {
        switch (evt.type) {
            case BABYLON.PointerEventTypes.POINTERDOWN:
                
                break;
            case BABYLON.PointerEventTypes.POINTERMOVE:

                if (this._currentLine && this._currentLine.measureMode != MeasurementType.Point) {
                    this._currentLine.endPosition = this._pickPoint(evt);
                    this._currentLine.isDirty = true;
                }
                
                break;
            case BABYLON.PointerEventTypes.POINTERUP:
                
                break;
            case BABYLON.PointerEventTypes.POINTERWHEEL:
                break;
            case BABYLON.PointerEventTypes.POINTERPICK:
                if (evt.event.button == 0) {
                    

                    const pickedPosition = this._pickPoint(evt);
                    if (pickedPosition) {
                        if (this._measureMode == MeasurementType.Distance ||
                            this._measureMode == MeasurementType.XOrtho ||
                            this._measureMode == MeasurementType.YOrtho ||
                            this._measureMode == MeasurementType.ZOrtho) {
                            if (this._currentLine == null) {
                                this._currentLine = { distance: 0, startPosition: pickedPosition, endPosition: null, text: null, valid: false, lines: [], textLine: null, isDirty: true, measureMode: this._measureMode };

                                //this._measurements.push(this._currentLine);
                            }
                            else {
                                this._currentLine.endPosition = this._pickPoint(evt);

                                this._currentLine.isDirty = true;

                                if (!this._currentLine.endPosition || !this._currentLine.valid) {
                                    //this._measurements.pop();
                                    this._disposeLineData(this._currentLine);
                                }
                                else {
                                    // Retire the transient placement graphic before the store-driven
                                    // measurement is rebuilt in the scene.
                                    this._disposeLineData(this._currentLine);
                                    this._createMeasurement(this._currentLine);
                                }
                                this._currentLine = null;
                            }
                        }
                        else if (this._measureMode == MeasurementType.Point) {
                            var point: IPointMeasurementInfo = { startPosition: pickedPosition, text: null, valid: false, lines: [], isDirty: true, measureMode: this._measureMode };

                            if (point.startPosition.realCoordinate != null) {
                                //this._measurements.push(point);
                                this._createMeasurement(point as any);
                            }
                        }
                    }
                }
                break;
            case BABYLON.PointerEventTypes.POINTERTAP:
                break;
            
        }
    }

    private _handleBeforeCameraRenderObservable = (camera: BABYLON.Camera) => {

        if (!ViewerStore.isDepthMapLoaded())
            return;

        this._measurements.forEach(measurement => {

            this._processGraphicsForMeasurement(measurement, camera);
        });

        if (this._currentLine != null) {
            this._processGraphicsForMeasurement(this._currentLine, camera);
        }
    }

    private _processGraphicsForMeasurement(measurement: IMeasurement, camera: BABYLON.Camera) {
        if (measurement.measureMode != MeasurementType.Point) {

            var line: ILineInfo = measurement as any;
            // Since the positions are based on clicks, we update the positions of the screen so the arrow heads
            // can be correctly drawn.
            if (line.endPosition != null) {
                var endScreen = BABYLON.Vector3.Project(
                    line.endPosition.imageCoordinate,
                    BABYLON.Matrix.Identity(),
                    this.scene.getTransformMatrix(),
                    camera.viewport.toGlobal(this.engine.getRenderWidth(), this.engine.getRenderHeight())
                );
                line.endPosition.screenCoordinate = new BABYLON.Vector2(endScreen.x, endScreen.y);
            }
            if (line.startPosition != null) {
                var startScreen = BABYLON.Vector3.Project(
                    line.startPosition.imageCoordinate,
                    BABYLON.Matrix.Identity(),
                    this.scene.getTransformMatrix(),
                    camera.viewport.toGlobal(this.engine.getRenderWidth(), this.engine.getRenderHeight())
                );

                line.startPosition.screenCoordinate = new BABYLON.Vector2(startScreen.x, startScreen.y);
            }


            if (line.startPosition != null && line.endPosition != null) {

                this._updateLine(line, camera);
            }
        }
        else {
            var pointMeasurement: IPointMeasurementInfo = measurement as any;

            if (pointMeasurement.startPosition != null) {
                var startScreen = BABYLON.Vector3.Project(
                    pointMeasurement.startPosition.imageCoordinate,
                    BABYLON.Matrix.Identity(),
                    this.scene.getTransformMatrix(),
                    camera.viewport.toGlobal(this.engine.getRenderWidth(), this.engine.getRenderHeight())
                );

                pointMeasurement.startPosition.screenCoordinate = new BABYLON.Vector2(startScreen.x, startScreen.y);
            }


            if (pointMeasurement.startPosition != null) {
                var startSeen = camera.isInFrustum(new BABYLON.BoundingInfo(pointMeasurement.startPosition.imageCoordinate, pointMeasurement.startPosition.imageCoordinate));

                if(startSeen)
                    this._updatePointMeasurement(pointMeasurement, startSeen);
            }
        }
    }
   
    private _updatePointMeasurement(point: IPointMeasurementInfo, startSeen: boolean) {

        if (point.startPosition != null) {

            if (point.lines.length > 0) {

                this._guiTexture.removeControl(point.text);
                point.text.dispose();

                for (var lIndex = 0; lIndex < point.lines.length; lIndex++) {
                    this.scene.removeMesh(point.lines[lIndex]);
                    point.lines[lIndex].dispose();
                }

                point.lines = [];
            }

            var p1 = this.scene.pick(point.startPosition.screenCoordinate.x - 10, point.startPosition.screenCoordinate.y);
            var p2 = this.scene.pick(point.startPosition.screenCoordinate.x + 10, point.startPosition.screenCoordinate.y);
            var p3 = this.scene.pick(point.startPosition.screenCoordinate.x, point.startPosition.screenCoordinate.y - 10);
            var p4 = this.scene.pick(point.startPosition.screenCoordinate.x, point.startPosition.screenCoordinate.y + 10);

            var textPos = this.scene.pick(point.startPosition.screenCoordinate.x, point.startPosition.screenCoordinate.y - 50);
            var linePoints1 = [p1.pickedPoint, p2.pickedPoint];
            var linePoints2 = [p3.pickedPoint, p4.pickedPoint];

            // Create lines from points
            var acrossLine = BABYLON.MeshBuilder.CreateLines("pointMeasurement", { points: linePoints1, colors: this._validMeasurementColors,  updatable: true }, this.scene);
            var downLine = BABYLON.MeshBuilder.CreateLines("pointMeasurement", { points: linePoints2, colors: this._validMeasurementColors, updatable: true }, this.scene);
            acrossLine.renderingGroupId = 1;
            acrossLine.isPickable = false;
            acrossLine.enableEdgesRendering();
            acrossLine.edgesWidth = 3;
            downLine.renderingGroupId = 1;
            downLine.isPickable = false;

            var text = new BABYLONGUI.TextBlock("PointText", "");
            text.color = this._validColor.toHexString();
            text.resizeToFit = true;
            text.text = "N " + ViewerStore.convertToUserUnit(point.startPosition.realCoordinate.z) + "\n" +
                "W " + ViewerStore.convertToUserUnit(point.startPosition.realCoordinate.x) + "\n" +
                "U " + ViewerStore.convertToUserUnit(point.startPosition.realCoordinate.y);
            this._guiTexture.addControl(text);

            point.text = text;
            var topPoint = BABYLON.Mesh.CreateSphere("textPoint", 1, 1);
            topPoint.position = textPos.pickedPoint;
            topPoint.setEnabled(false);
            text.linkWithMesh(topPoint);
            //acrossLine.edgesWidth = 10;

            point.lines.push(acrossLine);
            point.lines.push(downLine);
        }
    }

    private _updateLine(line: ILineInfo, camera: BABYLON.Camera) {

        var orthoMode = line.measureMode;
        {
            
            if (line.startPosition != null && line.endPosition != null) {

                
                // check if measurement is valid. If it is not, draw ortho measurement as noraml red line
                var pointsValid = line.startPosition.realCoordinate && line.endPosition.realCoordinate;

                // draw lines
                if (orthoMode == MeasurementType.Distance || !pointsValid) {

                    var startPosition = line.startPosition;
                    var endPosition = line.endPosition;

                    var startSeen = camera.isInFrustum(new BABYLON.BoundingInfo(line.startPosition.imageCoordinate, line.startPosition.imageCoordinate));
                    var endSeen = camera.isInFrustum(new BABYLON.BoundingInfo(line.endPosition.imageCoordinate, line.endPosition.imageCoordinate));

                    var dir = new BABYLON.Vector2(endPosition.screenCoordinate.x - startPosition.screenCoordinate.x, endPosition.screenCoordinate.y - startPosition.screenCoordinate.y);

                    if (dir.length() > 0) {

                        line.valid = startPosition.realCoordinate && endPosition.realCoordinate ? true : false;

                        var length = Math.min(15, dir.length() * .5);
                        dir.normalize();
                        dir = dir.scale(length);

                        var flipStartHead = startSeen && !endSeen ? this.scene.pick(endPosition.screenCoordinate.x, endPosition.screenCoordinate.y).pickedPoint?.subtract(endPosition.imageCoordinate).length() > 1 : false;
                        var flipEndHead = !startSeen && endSeen ? this.scene.pick(startPosition.screenCoordinate.x, startPosition.screenCoordinate.y).pickedPoint?.subtract(startPosition.imageCoordinate).length() > 1 : false;

                        var rot1 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, Math.PI / 6);
                        var rot2 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, -1 * Math.PI / 6);
                        var rot3 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, Math.PI / 6);
                        var rot4 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, -1 * Math.PI / 6);

                        var pos1 = this.scene.pick(rot1.x, rot1.y);
                        var pos2 = this.scene.pick(rot2.x, rot2.y);
                        var pos3 = this.scene.pick(rot3.x, rot3.y);
                        var pos4 = this.scene.pick(rot4.x, rot4.y);

                        var centerPoint = startPosition.imageCoordinate.add(endPosition.imageCoordinate.subtract(startPosition.imageCoordinate).scale(.5));

                        if (pos1.pickedPoint == null || centerPoint == null || startPosition.imageCoordinate == null || pos2.pickedPoint == null || endPosition.imageCoordinate == null || pos3.pickedPoint == null || pos4.pickedPoint == null) {
                            return;
                        }
                        var linePoints;
                        var textLinePoints;
                        if (startSeen && endSeen) {
                            linePoints = [pos1.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), pos2.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos3.pickedPoint.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos4.pickedPoint.subtract(centerPoint)];
                        }
                        else if (startSeen) {
                            linePoints = [pos1.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), pos2.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];
                        }
                        else if (endSeen) {
                            linePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos3.pickedPoint.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos4.pickedPoint.subtract(centerPoint)];
                        }
                        else {
                            linePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];
                        }

                        textLinePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];

                        if (line.lines.length > 0) {

                            this.scene.removeMesh(line.textLine);
                            this._guiTexture.removeControl(line.text);
                            line.textLine.dispose();

                            for (var lIndex = 0; lIndex < line.lines.length; lIndex++) {
                                this.scene.removeMesh(line.lines[lIndex]);
                                line.text.dispose();
                                line.lines[lIndex].dispose();
                            }

                            line.lines = [];
                        }

                        line.lines.push(BABYLON.MeshBuilder.CreateLines("line", {
                            colors: line.valid ? this._validMeasurementColors : this._invalidMeasurementColors,
                            points: linePoints,
                            updatable: true
                        }, this.scene));

                        if (line != this._currentLine) {
                            var sphere1 = BABYLON.MeshBuilder.CreateSphere("startPointSphere", {
                                diameter: 5,
                            }, this.scene);

                            var sphere2 = BABYLON.MeshBuilder.CreateSphere("startPointSphere", {
                                diameter: 5,
                            }, this.scene);

                            sphere1.position = new BABYLON.Vector3(line.startPosition.imageCoordinate.x, line.startPosition.imageCoordinate.y, line.startPosition.imageCoordinate.z).normalize().scale(495);
                            sphere2.position = new BABYLON.Vector3(line.endPosition.imageCoordinate.x, line.endPosition.imageCoordinate.y, line.endPosition.imageCoordinate.z).normalize().scale(495);

                            sphere1.isPickable = false;
                            sphere2.isPickable = false;

                            sphere1.material = this._sphereMeasurementMaterial;
                            sphere2.material = this._sphereMeasurementMaterial;

                            line.lines.push(sphere1);
                            line.lines.push(sphere2);
                        }

                        // Yes, this is silly. We create a second disabled line for the text. WHy don't we use the line above to link to the mesh?
                        // This is done because there are nasty bugs when the line has hidden points, and we handle the head drawing. The position on
                        // which the text gets anchored is off in that case. Therefore, we create a dummy 2 point line for the text.
                        line.textLine = BABYLON.MeshBuilder.CreateLines("textline", {
                            points: textLinePoints,
                            updatable: true
                        }, this.scene);

                        line.textLine.setEnabled(false);

                        var text = new BABYLONGUI.TextBlock("text", "");
                        text.color = this._validColor.toHexString();
                        text.resizeToFit = true;
                        text.text = "Text";
                        this._guiTexture.addControl(text);

                        text.isPointerBlocker = false;
                        line.text = text as any;
                        line.lines[0].isPickable = false;

                        line.text.linkWithMesh(line.textLine);

                        line.lines[0].renderingGroupId = 1;
                        line.lines[0].position = centerPoint;
                        line.textLine.position = centerPoint;
                        var distance = (line.valid) ? endPosition.realCoordinate.subtract(startPosition.realCoordinate).length() : 0;

                        var textDistance = (line.valid) ? endPosition.realCoordinate.subtract(startPosition.realCoordinate).length() * 1000: 0;
                        (line as any).distance = distance;
                        var flip = startPosition.screenCoordinate.x > endPosition.screenCoordinate.x;
                        var text: BABYLONGUI.TextBlock = line.text as BABYLONGUI.TextBlock;
                        var dir = new BABYLON.Vector2(endPosition.screenCoordinate.y - startPosition.screenCoordinate.y, endPosition.screenCoordinate.x - startPosition.screenCoordinate.x);

                        text.rotation = Math.atan2(dir.x, dir.y);
                        dir = dir.normalize();

                        text.linkOffsetX = dir.x * 10;
                        text.linkOffsetY = dir.y * 10 * -1;

                        if (flip) {
                            text.rotation += Math.PI;
                            text.linkOffsetX = dir.x * 10 * -1;
                            text.linkOffsetY = dir.y * 10;
                        }

                        text.text = line.valid ? ViewerStore.convertToUserUnit(distance) : "";
                    }
                    else {
                        line.valid = false;
                    }
                }
                else {

                    var pA;

                    if (orthoMode == MeasurementType.XOrtho) {
                        pA = new BABYLON.Vector3(line.endPosition.realCoordinate.x, line.startPosition.realCoordinate.y, line.startPosition.realCoordinate.z);
                    }
                    else if (orthoMode == MeasurementType.YOrtho) {
                        pA = new BABYLON.Vector3(line.startPosition.realCoordinate.x, line.endPosition.realCoordinate.y, line.startPosition.realCoordinate.z);
                    }
                    else if (orthoMode == MeasurementType.ZOrtho) {
                        pA = new BABYLON.Vector3(line.startPosition.realCoordinate.x, line.startPosition.realCoordinate.y, line.endPosition.realCoordinate.z);
                    }

                    var paInfo = this._viewer.worldCoordinateInformation(pA);

                    var aScreen = BABYLON.Vector3.Project(
                        paInfo.imageCoordinate,
                        BABYLON.Matrix.Identity(),
                        this.scene.getTransformMatrix(),
                        this.scene.activeCamera.viewport.toGlobal(this.engine.getRenderWidth(), this.engine.getRenderHeight())
                    );

                    var pointA = { realCoordinate: pA, imageCoordinate: paInfo.imageCoordinate, screenCoordinate: new BABYLON.Vector2(aScreen.x, aScreen.y) };

                    var pB;
                    if (orthoMode == MeasurementType.XOrtho) {
                        pB = new BABYLON.Vector3(line.endPosition.realCoordinate.x, line.endPosition.realCoordinate.y, line.startPosition.realCoordinate.z);
                    }
                    else if (orthoMode == MeasurementType.YOrtho) {
                        pB = new BABYLON.Vector3(line.endPosition.realCoordinate.x, line.endPosition.realCoordinate.y, line.startPosition.realCoordinate.z);
                    }
                    else if (orthoMode == MeasurementType.ZOrtho) {
                        pB = new BABYLON.Vector3(line.endPosition.realCoordinate.x, line.startPosition.realCoordinate.y, line.endPosition.realCoordinate.z);
                    }

                    
                    var pbInfo = this._viewer.worldCoordinateInformation(pB);
                    var bScreen = BABYLON.Vector3.Project(
                        pbInfo.imageCoordinate,
                        BABYLON.Matrix.Identity(),
                        this.scene.getTransformMatrix(),
                        this.scene.activeCamera.viewport.toGlobal(this.engine.getRenderWidth(), this.engine.getRenderHeight())
                    );

                    var pointB = { realCoordinate: pB, imageCoordinate: pbInfo.imageCoordinate, screenCoordinate: new BABYLON.Vector2(bScreen.x, bScreen.y) };

                    var startSeen = camera.isInFrustum(new BABYLON.BoundingInfo(line.startPosition.imageCoordinate, line.startPosition.imageCoordinate));
                    var endSeen = camera.isInFrustum(new BABYLON.BoundingInfo(pointA.imageCoordinate, pointA.imageCoordinate));

                    {
                        const startPosition = line.startPosition;
                        const endPosition = pointA;

                        var dir = new BABYLON.Vector2(endPosition.screenCoordinate.x - startPosition.screenCoordinate.x, endPosition.screenCoordinate.y - startPosition.screenCoordinate.y);

                        if (dir.length() > 0) {

                            line.valid = startPosition.realCoordinate && endPosition.realCoordinate ? true : false;

                            var length = Math.min(15, dir.length() * .5);
                            dir.normalize();
                            dir = dir.scale(length);

                            var flipStartHead = startSeen && !endSeen ? this.scene.pick(endPosition.screenCoordinate.x, endPosition.screenCoordinate.y).pickedPoint?.subtract(endPosition.imageCoordinate).length() > 1 : false;
                            var flipEndHead = !startSeen && endSeen ? this.scene.pick(startPosition.screenCoordinate.x, startPosition.screenCoordinate.y).pickedPoint?.subtract(startPosition.imageCoordinate).length() > 1 : false;

                            var rot1 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, Math.PI / 6);
                            var rot2 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, -1 * Math.PI / 6);
                            var rot3 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, Math.PI / 6);
                            var rot4 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, -1 * Math.PI / 6);

                            var pos1 = this.scene.pick(rot1.x, rot1.y);
                            var pos2 = this.scene.pick(rot2.x, rot2.y);
                            var pos3 = this.scene.pick(rot3.x, rot3.y);
                            var pos4 = this.scene.pick(rot4.x, rot4.y);

                            var centerPoint = startPosition.imageCoordinate.add(endPosition.imageCoordinate.subtract(startPosition.imageCoordinate).scale(.5));

                            if (pos1.pickedPoint == null || centerPoint == null || startPosition.imageCoordinate == null || pos2.pickedPoint == null || endPosition.imageCoordinate == null || pos3.pickedPoint == null || pos4.pickedPoint == null) {
                                return;
                            }
                            var linePoints;
                            var textLinePoints;
                            if (startSeen && endSeen) {
                                linePoints = [pos1.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), pos2.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos3.pickedPoint.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos4.pickedPoint.subtract(centerPoint)];
                            }
                            else if (startSeen) {
                                linePoints = [pos1.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), pos2.pickedPoint.subtract(centerPoint), startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];
                            }
                            else if (endSeen) {
                                linePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos3.pickedPoint.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint), pos4.pickedPoint.subtract(centerPoint)];
                            }
                            else {
                                linePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];
                            }

                            textLinePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];

                            if (line.lines.length > 0) {

                                this.scene.removeMesh(line.textLine);
                                this._guiTexture.removeControl(line.text);
                                line.textLine.dispose();

                                for (var lIndex = 0; lIndex < line.lines.length; lIndex++) {
                                    this.scene.removeMesh(line.lines[lIndex]);
                                    line.text.dispose();
                                    line.lines[lIndex].dispose();
                                }

                                line.lines = [];
                            }

                            line.lines.push(BABYLON.MeshBuilder.CreateLines("line", {
                                colors: line.valid ? this._validMeasurementColors : this._invalidMeasurementColors,
                                points: linePoints,
                                updatable: true
                            }, this.scene));

                            // Yes, this is silly. We create a second disabled line for the text. WHy don't we use the line above to link to the mesh?
                            // This is done because there are nasty bugs when the line has hidden points, and we handle the head drawing. The position on
                            // which the text gets anchored is off in that case. Therefore, we create a dummy 2 point line for the text.
                            line.textLine = BABYLON.MeshBuilder.CreateLines("textline", {
                                points: textLinePoints,
                                updatable: true
                            }, this.scene);

                            if (line != this._currentLine) {
                                var sphere1 = BABYLON.MeshBuilder.CreateSphere("startPointSphere", {
                                    diameter: 5,
                                }, this.scene);

                                var sphere2 = BABYLON.MeshBuilder.CreateSphere("startPointSphere", {
                                    diameter: 5,
                                }, this.scene);

                                sphere1.position = new BABYLON.Vector3(line.startPosition.imageCoordinate.x, line.startPosition.imageCoordinate.y, line.startPosition.imageCoordinate.z).normalize().scale(495);
                                sphere2.position = new BABYLON.Vector3(line.endPosition.imageCoordinate.x, line.endPosition.imageCoordinate.y, line.endPosition.imageCoordinate.z).normalize().scale(495);

                                sphere1.isPickable = false;
                                sphere2.isPickable = false;

                                sphere1.material = this._sphereMeasurementMaterial;
                                sphere2.material = this._sphereMeasurementMaterial;

                                line.lines.push(sphere1);
                                line.lines.push(sphere2);
                            }
                            
                            line.textLine.setEnabled(false);

                            var text = new BABYLONGUI.TextBlock("text", "");
                            text.color = this._validColor.toHexString();
                            text.resizeToFit = true;
                            text.text = "Text";
                            this._guiTexture.addControl(text);

                            text.isPointerBlocker = false;
                            line.text = text as any;
                            line.lines[0].isPickable = false;

                            line.text.linkWithMesh(line.textLine);

                            line.lines[0].renderingGroupId = 1;
                            line.lines[0].position = centerPoint;
                            line.textLine.position = centerPoint;
                            const distance = (line.valid) ? endPosition.realCoordinate.subtract(startPosition.realCoordinate).length() : 0;
                            //var textDistance = (line.valid) ? endPosition.realCoordinate.subtract(startPosition.realCoordinate).length() * 1000 : 0;
                            (line as any).distance = distance;
                            var flip = startPosition.screenCoordinate.x > endPosition.screenCoordinate.x;
                            var text: BABYLONGUI.TextBlock = line.text as BABYLONGUI.TextBlock;
                            var dir = new BABYLON.Vector2(endPosition.screenCoordinate.y - startPosition.screenCoordinate.y, endPosition.screenCoordinate.x - startPosition.screenCoordinate.x);

                            text.rotation = Math.atan2(dir.x, dir.y);
                            dir = dir.normalize();

                            text.linkOffsetX = dir.x * 10;
                            text.linkOffsetY = dir.y * 10 * -1;

                            if (flip) {
                                text.rotation += Math.PI;
                                text.linkOffsetX = dir.x * 10 * -1;
                                text.linkOffsetY = dir.y * 10;
                            }

                            text.text = line.valid ? ViewerStore.convertToUserUnit(distance) : "";
                        }
                        else {
                            line.valid = false;
                        }
                    }
                    if (line.valid) {
                        {
                            const startPosition = pointA;
                            const endPosition = pointB;

                            var dir = new BABYLON.Vector2(endPosition.screenCoordinate.x - startPosition.screenCoordinate.x, endPosition.screenCoordinate.y - startPosition.screenCoordinate.y);
                            var numDashes = Math.max(2, dir.length() / 10);
                            if (dir.length() > 0) {

                                line.valid = startPosition.realCoordinate && endPosition.realCoordinate ? true : false;

                                var length = Math.min(15, dir.length() * .5);
                                dir.normalize();
                                dir = dir.scale(length);

                                var flipStartHead = startSeen && !endSeen ? this.scene.pick(endPosition.screenCoordinate.x, endPosition.screenCoordinate.y).pickedPoint?.subtract(endPosition.imageCoordinate).length() > 1 : false;
                                var flipEndHead = !startSeen && endSeen ? this.scene.pick(startPosition.screenCoordinate.x, startPosition.screenCoordinate.y).pickedPoint?.subtract(startPosition.imageCoordinate).length() > 1 : false;

                                var rot1 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, Math.PI / 6);
                                var rot2 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, -1 * Math.PI / 6);
                                var rot3 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, Math.PI / 6);
                                var rot4 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, -1 * Math.PI / 6);

                                var pos1 = this.scene.pick(rot1.x, rot1.y);
                                var pos2 = this.scene.pick(rot2.x, rot2.y);
                                var pos3 = this.scene.pick(rot3.x, rot3.y);
                                var pos4 = this.scene.pick(rot4.x, rot4.y);

                                const centerPoint = startPosition.imageCoordinate.add(endPosition.imageCoordinate.subtract(startPosition.imageCoordinate).scale(.5));

                                if (pos1.pickedPoint == null || centerPoint == null || startPosition.imageCoordinate == null || pos2.pickedPoint == null || endPosition.imageCoordinate == null || pos3.pickedPoint == null || pos4.pickedPoint == null) {
                                    return;
                                }
                                var linePoints;
                                var textLinePoints;

                                linePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];


                                var dotLine = (BABYLON.MeshBuilder.CreateDashedLines("line", {
                                    points: linePoints,
                                    updatable: true,
                                    dashNb: numDashes
                                    
                                }, this.scene));
                                
                                if (line != this._currentLine) {
                                    dotLine.color = this._validColor;
                                }

                                //dotLine.material = line.valid ? this._validMeasurementMat : this._invalidMeasurementMat;
                                dotLine.isPickable = false;
                                dotLine.renderingGroupId = 1;
                                dotLine.position = centerPoint;

                                line.lines.push(dotLine);
                            }
                            else {
                                line.valid = false;
                            }
                        }
                        {
                            const startPosition = pointB;
                            const endPosition = line.endPosition;

                            var dir = new BABYLON.Vector2(endPosition.screenCoordinate.x - startPosition.screenCoordinate.x, endPosition.screenCoordinate.y - startPosition.screenCoordinate.y);
                            var numDashes = Math.max(2, dir.length() / 10);
                            if (dir.length() > 0) {

                                line.valid = startPosition.realCoordinate && endPosition.realCoordinate ? true : false;

                                var length = Math.min(15, dir.length() * .5);
                                dir.normalize();
                                dir = dir.scale(length);

                                var flipStartHead = startSeen && !endSeen ? this.scene.pick(endPosition.screenCoordinate.x, endPosition.screenCoordinate.y).pickedPoint?.subtract(endPosition.imageCoordinate).length() > 1 : false;
                                var flipEndHead = !startSeen && endSeen ? this.scene.pick(startPosition.screenCoordinate.x, startPosition.screenCoordinate.y).pickedPoint?.subtract(startPosition.imageCoordinate).length() > 1 : false;

                                var rot1 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, Math.PI / 6);
                                var rot2 = this._rotatePoint(startPosition.screenCoordinate.add(!flipStartHead ? dir : dir.scale(-1)), startPosition.screenCoordinate, -1 * Math.PI / 6);
                                var rot3 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, Math.PI / 6);
                                var rot4 = this._rotatePoint(endPosition.screenCoordinate.add(!flipEndHead ? dir.scale(-1) : dir), endPosition.screenCoordinate, -1 * Math.PI / 6);

                                var pos1 = this.scene.pick(rot1.x, rot1.y);
                                var pos2 = this.scene.pick(rot2.x, rot2.y);
                                var pos3 = this.scene.pick(rot3.x, rot3.y);
                                var pos4 = this.scene.pick(rot4.x, rot4.y);

                                const centerPoint = startPosition.imageCoordinate.add(endPosition.imageCoordinate.subtract(startPosition.imageCoordinate).scale(.5));

                                if (pos1.pickedPoint == null || centerPoint == null || startPosition.imageCoordinate == null || pos2.pickedPoint == null || endPosition.imageCoordinate == null || pos3.pickedPoint == null || pos4.pickedPoint == null) {
                                    return;
                                }
                                var linePoints;
                                var textLinePoints;

                                linePoints = [startPosition.imageCoordinate.subtract(centerPoint), endPosition.imageCoordinate.subtract(centerPoint)];


                                var dotLine = (BABYLON.MeshBuilder.CreateDashedLines("line", {
                                    points: linePoints,
                                    updatable: true,
                                    dashNb: numDashes

                                }, this.scene));

                                if (line != this._currentLine) {
                                    dotLine.color = this._validColor;
                                }

                                //dotLine.material = line.valid ? this._validMeasurementMat : this._invalidMeasurementMat;
                                dotLine.isPickable = false;
                                dotLine.renderingGroupId = 1;
                                dotLine.position = centerPoint;

                                line.lines.push(dotLine);
                            }
                            else {
                                line.valid = false;
                            }
                        }
                    }
                }
            }
            else {
                this._disposeLineData(line);
            }

            line.isDirty = false;
        }
    }

    private _rotatePoint(point: BABYLON.Vector2, center: BABYLON.Vector2, angle: number): BABYLON.Vector2 {
        const x = center.x + (point.x - center.x) * Math.cos(angle) - (point.y - center.y) * Math.sin(angle);
        const y = center.y + (point.x - center.x) * Math.sin(angle) + (point.y - center.y) * Math.cos(angle);
        return new BABYLON.Vector2(x, y);
    }
    private _disposeMeasurement(measuremnt: IMeasurement) {

        /*
        if (measuremnt.measureMode == MeasurementType.Point) {
            this._disposePointMeasurement(measuremnt as any);
        }
        else {
            this._disposeLineData(measuremnt as any);
        }
        */

        for (var x = 0; x < measuremnt.lines.length; x++) {
            measuremnt.lines[x].dispose();
        }
        measuremnt.text?.dispose();
        measuremnt.text = null;
        measuremnt.lines = [];
    }

    private _disposePointMeasurement(pointMeasurement: IPointMeasurementInfo) {

        //pointMeasurement.sphere.dispose();
        pointMeasurement.text?.dispose();
        pointMeasurement.text = null;
    }

    private _disposeLineData(line: ILineInfo) {

        if (line.text) {
            this._guiTexture.removeControl(line.text);
        }
        if (line.textLine) {
            this.scene.removeMesh(line.textLine);
            line.textLine.dispose();
            line.textLine = null;
        }

        for (var x = 0; x < line.lines.length; x++) {
            line.lines[x].dispose();
        }
        line.text?.dispose();
        line.text = null;
        line.lines = [];
    }
}

export default MeasurementManager;
